import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "@hooks/use-translations.ts";
import { useBreakpoint } from "@hooks/use-breakpoint.ts";
import { useHouseholdInformation } from "@providers/household-information-provider.tsx";
import { useCallback, useMemo, useState } from "react";
import { ContactInformationData } from "@components/forms/contact-information/types.ts";
import { HouseholdQuestionnaireContainer } from "@components/form/specialised/household-questionnaire/household-questionnaire-container.tsx";
import { HouseholdQuestionnaire } from "@components/form/specialised/household-questionnaire/household-questionnaire.tsx";
import { SimpleSavingsChart } from "@components/charts/simple-savings-chart/simple-savings-chart.tsx";
import { Card, CardContent } from "@components/ui/card.tsx";
import { Savings } from "@components/tool-messaging/savings.tsx";
import { Button } from "@components/ui/button/button.tsx";
import { SavingsMethodologyDialog } from "@components/tool-messaging/savings-methodology.tsx";
import { LeadCreatedDialog } from "@components/specialised/lead-created/lead-created-dialog.tsx";
import { Header } from "@components/ui/header.tsx";
import { useHouseholdInformationStore } from "@stores/household-information-store.ts";
import { DialogTrigger } from "@components/ui/dialog";
import { ContactInformationDialog } from "@components/forms/contact-information/contact-information-dialog.tsx";
import { useSystemConfiguration } from "@stores/system-configuration-store.ts";
import { useShallow } from "zustand/react/shallow";

export const Route = createFileRoute("/calculator")({
  component: Calculator,
});

function Calculator() {
  const householdPostcode = useHouseholdInformationStore(
    (state) => state.postcode,
  );

  const customAnnualConsumption = useHouseholdInformationStore(
    (state) => state.annualConsumption,
  );

  const meterType = useHouseholdInformationStore((state) => state.meterType);

  const systemConfiguration = useSystemConfiguration(
    useShallow((state) => ({
      batteryCapacity: state.batteryCapacity,
    })),
  );

  const [showContactInformation, setShowContactInformation] = useState(false);
  const [showLeadCreated, setShowLeadCreated] = useState(false);
  const { translate } = useTranslations();
  const shouldUseLargeVariant = useBreakpoint("2xl");
  const isDesktopView = useBreakpoint("lg");
  const { simpleSavingsQuery, createLead, userId } = useHouseholdInformation();

  const chartStatus = useMemo(() => {
    if (simpleSavingsQuery.isFetching) {
      return {
        status: "loading",
      } as const;
    }

    if (simpleSavingsQuery.isError) {
      return {
        status: "error",
        tooltip: translate("savings.error-fetching-savings"),
      } as const;
    }

    return {
      status: undefined,
    } as const;
  }, [
    simpleSavingsQuery.isFetching,
    simpleSavingsQuery.isError,
    simpleSavingsQuery.error,
  ]);

  const onSubmitContactInformation = useCallback(
    async (contactInformation: ContactInformationData) => {
      await createLead(contactInformation);
      setShowContactInformation(false);
      setShowLeadCreated(true);
    },
    [createLead, setShowLeadCreated],
  );

  return (
    <div className="min-h-[min(100dvh,100rem)] mx-auto w-full max-w-[120rem] flex flex-col lg:flex-row gap-4 p-4 lg:p-8 lg:gap-8 motion-safe:[view-transition-name:main-content]">
      <div className="flex flex-col gap-4 lg:gap-8">
        <Header>{translate("calculator.title")}</Header>
        {isDesktopView && (
          <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 min-h-0 flex-grow lg:shrink-0">
            <HouseholdQuestionnaireContainer
              onOpenContactInformation={() => setShowContactInformation(true)}
            >
              <HouseholdQuestionnaire />
            </HouseholdQuestionnaireContainer>
          </div>
        )}
      </div>
      <div className="flex flex-grow gap-4 lg:gap-8 min-h-0">
        <div className="flex flex-col flex-grow gap-4 lg:gap-8 min-h-0">
          <SimpleSavingsChart data={simpleSavingsQuery.data} {...chartStatus} />
          <Card>
            <CardContent className="flex flex-col lg:flex-row gap-4 lg:gap-8 items-center justify-between">
              <div className="flex flex-grow w-full items-center flex-col gap-4">
                <Savings
                  savings={simpleSavingsQuery.data?.totalSavingsWithVat}
                />
              </div>
              <div className="flex flex-col text-center 2xl:flex-row-reverse gap-4 lg:gap-8 items-center">
                {isDesktopView && (
                  <Button
                    size="md"
                    grow={!shouldUseLargeVariant}
                    onClick={() => setShowContactInformation(true)}
                  >
                    {translate("calculator.request-a-quote")}
                  </Button>
                )}
                <SavingsMethodologyDialog
                  savings={simpleSavingsQuery.data?.totalSavingsWithVat}
                  annualConsumption={
                    customAnnualConsumption ??
                    simpleSavingsQuery.data?.meta.annualConsumptionKwh
                  }
                  customAnnualConsumption={customAnnualConsumption !== null}
                  postcode={householdPostcode}
                  tariffUsedForLastYear={
                    simpleSavingsQuery.data?.meta.tariffUsedForLastYear ??
                    "market"
                  }
                  tariffUsedForComparison={
                    simpleSavingsQuery.data?.meta.tariffUsedForComparison ??
                    "market"
                  }
                  hasSmartMeter={meterType === "Smart"}
                  batteryCapacity={systemConfiguration.batteryCapacity}
                  trigger={
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size={shouldUseLargeVariant ? "md" : "sm"}
                        grow={!shouldUseLargeVariant}
                        onClick={() => {
                          window.gtag("event", "opened_methodology", {
                            userId,
                          });
                        }}
                      >
                        {translate(
                          "methodology-messaging.how-we-worked-it-out",
                        )}
                      </Button>
                    </DialogTrigger>
                  }
                />
              </div>
            </CardContent>
          </Card>
          {!isDesktopView && (
            <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 min-h-0">
              <HouseholdQuestionnaireContainer
                onOpenContactInformation={() => setShowContactInformation(true)}
              >
                <HouseholdQuestionnaire />
              </HouseholdQuestionnaireContainer>
            </div>
          )}
        </div>
      </div>
      <ContactInformationDialog
        open={showContactInformation}
        onOpenChange={(isOpen) => setShowContactInformation(isOpen)}
        postcode={householdPostcode}
        className="flex flex-col gap-6"
        onSubmit={onSubmitContactInformation}
      />
      <LeadCreatedDialog
        open={showLeadCreated}
        onOpenChange={(isOpen) => setShowLeadCreated(isOpen)}
      />
    </div>
  );
}
